import { useState, useEffect, useRef } from 'react';
import { X, Mail, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useChangeEmail,
  useSendChangeEmailOtp,
} from '../../api/staff/staffQueries.js';

export default function ChangeEmailModal({
  isOpen,
  onClose,
  currentEmail = 'user@example.com',
}) {
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState([
    '',
    '',
    '',
    '',
    '',
    '',
  ]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const { mutate: sendChangeEmailOTP, isPending } = useSendChangeEmailOtp();

  const { mutate: changeEmail, isPending: changeEmailLoading } =
    useChangeEmail();

  const codeInputRefs = useRef([]);

  useEffect(() => {
    let timer;
    if (isCountdownActive && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setIsCountdownActive(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, isCountdownActive]);

  // Reset the modal state when it's closed
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(1);
        setNewEmail('');
        setVerificationCode(['', '', '', '', '', '']);
        setError('');
        setSuccess(false);
      }, 300);
    }
  }, [isOpen]);

  // Focus the first digit input when step 2 is shown
  useEffect(() => {
    if (step === 2 && codeInputRefs.current[0]) {
      setTimeout(() => {
        codeInputRefs.current[0]?.focus();
      }, 300);
    }
  }, [step]);

  if (!isOpen) return null;

  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmitEmail = async (e) => {
    e.preventDefault();
    setError('');

    if (!newEmail) {
      setError('Please enter a valid email address');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    // Simulate API call to send verification code
    try {
      sendChangeEmailOTP(newEmail);
      setStep(2);
      // Start countdown for resend code
      setCountdown(60);
      setIsCountdownActive(true);
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');

    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit verification code');
      return;
    }

    // Simulate API call to verify code
    try {
      changeEmail({
        otp: code,
        newEmail,
      });
      setSuccess(true);

      // Auto close after success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError('Invalid verification code. Please try again.');
    }
  };

  const handleResendCode = async () => {
    if (isCountdownActive) return;

    // Simulate API call to resend verification code
    try {
      sendChangeEmailOTP(newEmail);
      setCountdown(60);
      setIsCountdownActive(true);
      // Clear the verification code inputs
      setVerificationCode(['', '', '', '', '', '']);
      // Focus the first input
      setTimeout(() => {
        codeInputRefs.current[0]?.focus();
      }, 100);
    } catch (err) {
      setError('Failed to resend verification code. Please try again.');
    }
  };

  const handleCodeInputChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    // Update the verification code array
    const newCode = [...verificationCode];
    newCode[index] = value.slice(0, 1); // Only take the first character

    setVerificationCode(newCode);

    // Auto-advance to next input if a digit was entered
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }

    // Allow navigation with arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');

    const newCode = [...verificationCode];
    digits.forEach((digit, index) => {
      if (index < 6) {
        newCode[index] = digit;
      }
    });

    setVerificationCode(newCode);

    // Focus the appropriate input after pasting
    if (digits.length < 6) {
      codeInputRefs.current[digits.length]?.focus();
    }
  };

  const goBack = () => {
    setStep(1);
  };

  // Animation variants
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={handleOutsideClick}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative overflow-hidden"
      >
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4 z-10"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>

        {/* Step indicator at the top */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#8147E7]/10">
            <motion.div
              className={`h-2.5 w-2.5 rounded-full ${
                step === 1 ? 'bg-[#8147E7]' : 'bg-gray-300'
              }`}
              animate={{ scale: step === 1 ? 1.2 : 1 }}
              transition={{ duration: 0.3 }}
            />
            <motion.div
              className={`h-2.5 w-2.5 rounded-full ${
                step === 2 ? 'bg-[#8147E7]' : 'bg-gray-300'
              }`}
              animate={{ scale: step === 2 ? 1.2 : 1 }}
              transition={{ duration: 0.3 }}
            />
            <span className="text-sm font-medium text-[#8147E7] ml-1">
              Step {step} of 2
            </span>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Change Email Address
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {step === 1
              ? 'Enter your new email address below'
              : 'Enter the verification code sent to your new email'}
          </p>
        </div>

        <AnimatePresence mode="wait" initial={false} custom={step}>
          {success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center justify-center py-6"
            >
              <div className="h-12 w-12 rounded-full bg-[#8147E7]/20 flex items-center justify-center mb-4">
                <Check className="h-6 w-6 text-[#8147E7]" />
              </div>
              <h3 className="text-lg font-medium mb-2 text-gray-900">
                Email Updated Successfully
              </h3>
              <p className="text-sm text-gray-500 text-center">
                Your email has been changed to {newEmail}
              </p>
            </motion.div>
          ) : step === 1 ? (
            <motion.div
              key="step1"
              custom={1}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <form onSubmit={handleSubmitEmail}>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current-email" className="text-gray-700">
                      Current Email
                    </Label>
                    <Input
                      id="current-email"
                      value={currentEmail}
                      disabled
                      className="bg-gray-50"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new-email" className="text-gray-700">
                      New Email
                    </Label>
                    <div className="relative">
                      <Input
                        id="new-email"
                        type="email"
                        placeholder="Enter your new email address"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="pl-10 border-gray-300 focus:border-[#8147E7] focus:ring-[#8147E7]"
                      />
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-500 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-[#8147E7] hover:bg-[#8147E7]/90 text-white"
                    disabled={isPending}
                  >
                    {isPending ? 'Sending Code...' : 'Continue'}
                    {!isPending && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </div>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              custom={-1}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <form onSubmit={handleVerifyCode}>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm mb-4 text-gray-600">
                      We've sent a verification code to{' '}
                      <span className="font-medium text-gray-900">
                        {newEmail}
                      </span>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="verification-code-0"
                      className="text-gray-700"
                    >
                      Verification Code
                    </Label>
                    <div className="flex justify-between gap-2">
                      {verificationCode.map((digit, index) => (
                        <div key={index} className="w-full">
                          <Input
                            id={`verification-code-${index}`}
                            ref={(el) => (codeInputRefs.current[index] = el)}
                            value={digit}
                            onChange={(e) =>
                              handleCodeInputChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleCodeKeyDown(index, e)}
                            onPaste={index === 0 ? handleCodePaste : undefined}
                            className="text-center px-0 text-lg font-medium border-gray-300 focus:border-[#8147E7] focus:ring-[#8147E7]"
                            maxLength={1}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-500 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                      onClick={goBack}
                      disabled={changeEmailLoading}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-[#8147E7] hover:bg-[#8147E7]/90 text-white"
                      disabled={changeEmailLoading}
                    >
                      {changeEmailLoading ? 'Verifying...' : 'Verify'}
                    </Button>
                  </div>

                  <Button
                    type="button"
                    variant="link"
                    className="w-full text-sm text-[#8147E7] hover:text-[#8147E7]/80"
                    disabled={isPending || isCountdownActive}
                    onClick={handleResendCode}
                  >
                    {isCountdownActive
                      ? `Resend code in ${countdown}s`
                      : 'Resend verification code'}
                  </Button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
